import { FC, MouseEvent, memo } from 'react';

import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';

import { applyPhoneMask } from '../../utils';
import { Subtitle, useStyles } from './CustomerInfo.styles';
import { CustomerInfoProps } from './CustomerInfo.types';

// Intercepts the link's default tel:/mailto: navigation when a handler is
// provided; without one the link keeps its native behavior.
const interceptClick = (handler?: () => void) =>
  handler
    ? (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        handler();
      }
    : undefined;

export const CustomerInfo: FC<CustomerInfoProps> = memo(
  ({
    id,
    name,
    clientType,
    email,
    phone,
    cell,
    address,
    referralSource,
    franchisee,
    franchiseLocation,
    onEmailClick,
    onPhoneClick,
    onCellClick,
  }) => {
    const styles = useStyles();

    return (
      <div className={styles.wrapper}>
        <Grid container spacing={2} direction="column">
          {name && (
            <Grid item>
              <Typography variant="h3" color="secondary" component="p" className="break-word">
                {name}
              </Typography>

              <Typography variant="body1" className="break-word">
                ID: {id}
              </Typography>
            </Grid>
          )}

          {clientType && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                Client type
              </Subtitle>

              <Typography variant="body1" className="break-word">
                {clientType}
              </Typography>
            </Grid>
          )}

          {email && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                E-mail
              </Subtitle>

              <Link
                href={`mailto:${email}`}
                onClick={interceptClick(onEmailClick)}
                variant="body1"
                color="textSecondary"
                className="break-word"
              >
                {email}
              </Link>
            </Grid>
          )}

          {phone && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                Phone number
              </Subtitle>

              <Link
                href={`tel:${phone}`}
                onClick={interceptClick(onPhoneClick)}
                variant="body1"
                color="textSecondary"
                className="break-word"
              >
                {applyPhoneMask(phone)}
              </Link>
            </Grid>
          )}

          {cell && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                Secondary phone number
              </Subtitle>

              <Link
                href={`tel:${cell}`}
                onClick={interceptClick(onCellClick)}
                variant="body1"
                color="textSecondary"
                className="break-word"
              >
                {applyPhoneMask(cell)}
              </Link>
            </Grid>
          )}

          {address && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                Address
              </Subtitle>

              <Typography variant="body1" className="break-word">
                {address}
              </Typography>
            </Grid>
          )}

          {referralSource && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                Referral source
              </Subtitle>

              <Typography variant="body1" className="break-word">
                {referralSource}
              </Typography>
            </Grid>
          )}

          {franchisee && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                Franchisee
              </Subtitle>

              <Typography variant="body1" className="break-word">
                {franchisee}
              </Typography>
            </Grid>
          )}

          {franchiseLocation && (
            <Grid item>
              <Subtitle variant="subtitle1" color="secondary">
                Franchise location
              </Subtitle>

              <Typography variant="body1" className="break-word">
                {franchiseLocation}
              </Typography>
            </Grid>
          )}
        </Grid>
      </div>
    );
  },
);
